import { useEffect, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { DropdownMenu2 } from '../DropdownMenu2';
import { DraggableMenuOptionItem } from './DraggableMenuOptionItem';

import type { ReactNode } from 'react';
import type { DropdownMenu2OptionItemData } from '../DropdownMenu2';
import type { DropdownMenuOptionItemData } from '../DropdownMenu2/types';
import type { DraggableDropdownMenu2Props, DraggableMenuOptionItemData } from './types';

/**
 * 드래그 가능한 드롭다운 메뉴
 *
 * DropdownMenu2를 기반으로 드래그 앤 드롭으로 아이템 순서를 변경할 수 있습니다.
 *
 * @example
 * ```tsx
 * const [items, setItems] = useState<DraggableMenuOptionItemData[]>([
 *   { id: '1', text: '항목 1' },
 *   { id: '2', text: '항목 2' },
 *   { id: '3', text: '항목 3' },
 * ]);
 *
 * <DraggableDropdownMenu2
 *   open={open}
 *   onClose={handleClose}
 *   anchorEl={anchorEl}
 *   items={items}
 *   onItemsReorder={setItems}
 * />
 * ```
 */
export function DraggableDropdownMenu2(props: DraggableDropdownMenu2Props) {
  const { items: initialItems, onItemsReorder, ...dropdownMenu2Props } = props;

  const [isDragging, setIsDragging] = useState(false);
  const [items, setItems] = useState(initialItems);
  const [insertLineState, setInsertLineState] = useState<{
    index: number;
    position: 'top' | 'bottom';
  } | null>(null);

  // props 변경 시 내부 상태 동기화
  useEffect(() => {
    setItems(initialItems);
  }, [initialItems]);

  const moveItem = (dragIndex: number, hoverIndex: number) => {
    const newItems = [...items];
    const draggedItem = newItems[dragIndex];

    newItems.splice(dragIndex, 1);
    newItems.splice(hoverIndex, 0, draggedItem);

    setItems(newItems);
    onItemsReorder?.(newItems);
  };

  const handleDragStart = (_itemId: string) => {
    setIsDragging(true);
  };

  const handleDragEnd = () => {
    setInsertLineState(null);
    setIsDragging(false);
  };

  const handleItemHover = (
    _dragIndex: number,
    hoverIndex: number,
    insertPosition: 'top' | 'bottom',
  ) => {
    setInsertLineState({
      index: hoverIndex,
      position: insertPosition,
    });
  };

  const handleItemHoverEnd = () => {
    setInsertLineState(null);
  };

  // renderItem을 생성하는 함수
  const createRenderItem = (
    originalItem: DraggableMenuOptionItemData,
    itemIndex: number,
  ): DropdownMenuOptionItemData['renderItem'] => {
    return (renderProps): ReactNode => {
      const {
        closeOnOptionClick: _closeOnOptionClick,
        focused,
        key,
        onClose: _onClose,
        ref,
        ...restProps
      } = renderProps as typeof renderProps & {
        closeOnOptionClick?: boolean;
        onClose?: unknown;
      };

      return (
        <DraggableMenuOptionItem
          key={key}
          ref={ref}
          dragId={originalItem.id}
          index={itemIndex}
          draggable={originalItem.draggable ?? true}
          isDraggingOnMenu={isDragging}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onHover={handleItemHover}
          onHoverEnd={handleItemHoverEnd}
          onMove={moveItem}
          showInsertLine={insertLineState?.index === itemIndex}
          insertPosition={insertLineState?.position}
          focused={focused}
          {...restProps}
        />
      );
    };
  };

  // items에 renderItem 주입
  const transformedItems = items.map((item, index) => ({
    ...item,
    renderItem: createRenderItem(item, index),
  })) as DropdownMenu2OptionItemData[];

  return (
    // context=globalThis 필수(제거 금지). react-dnd v16 DndProvider는 매니저를 globalThis에
    // 캐싱하지만 context 미지정(isGlobalInstance=true) 시 unmount cleanup이 그 캐시를 null로 지운다.
    // 로컬 dev는 react-dnd가 인스턴스 2개로 로드되고(layout vs hoisted peer-hash 사본) StrictMode
    // 이중 마운트로 한 Provider의 cleanup이 공유 캐시를 null → 다른 Provider가 두 번째 HTML5Backend를
    // setup → "Cannot have two HTML5 backends" 오류. context 지정 시 그 null-cleanup을 건너뛰어
    // 단일 매니저/Backend가 공유된다(prod은 production 빌드라 StrictMode 이중 마운트 없어 무관).
    <DndProvider backend={HTML5Backend} context={globalThis}>
      <DropdownMenu2 {...dropdownMenu2Props} items={transformedItems} />
    </DndProvider>
  );
}

DraggableDropdownMenu2.displayName = 'DraggableDropdownMenu2';
